import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { SpaceType } from 'src/components/SearchBar/types'
import { useFilterProvider } from 'src/context/FilterProvider'

export const NEARBY_SEARCH_COUNTS = gql`
  query NearbySearchCounts($input: NearbySearchCountsInput!) {
    nearbySearchCounts(input: $input) {
      total
      byLocationType {
        locationType
        count
      }
      tier
      radiusUsed
      locationTypeFiltered
    }
  }
`

/**
 * Maps frontend SpaceType enum (underscores) to ES locationType (hyphens).
 * ES events store locationType as 'self-storage', 'parking', etc.
 * Only 4 of 6 SpaceType values have ES equivalents (ALL and LAND do not).
 */
export const SPACE_TYPE_TO_ES_LOCATION_TYPE: Partial<
  Record<SpaceType, string>
> = {
  [SpaceType.SELF_STORAGE]: 'self-storage',
  [SpaceType.PARKING]: 'parking',
  [SpaceType.WAREHOUSE]: 'warehouse',
  [SpaceType.INDUSTRIAL_WAREHOUSE]: 'industrial-warehouse'
}

export type NearbySearchCountsResult = {
  total: number
  byLocationType: { locationType: string; count: number }[]
  tier: number
  radiusUsed: number
  locationTypeFiltered: boolean
}

export type NearbySearchCountsData = {
  nearbySearchCounts: NearbySearchCountsResult
}

export type NearbySearchCountsVars = {
  input: {
    lat: number
    lng: number
    radiusKm?: number
    locationType?: string
    days?: number
  }
}

/**
 * Shared nearby-supply signal for the empty/sparse search stack. Prefers the
 * SSG-provided `staticData` (pillar/city/hood pages pass it down from
 * getStaticProps); otherwise self-queries by the current map point. Both
 * `HostCTA` and `CaptureLead` call this — Apollo dedupes the identical
 * in-flight query, so the second consumer costs no extra network round trip.
 * Returns `undefined` while loading/skipped (no lat/lng) — callers must treat
 * that as "unknown", NOT as "zero supply".
 */
export const useNearbySearchCounts = (
  staticData?: NearbySearchCountsResult
): NearbySearchCountsResult | undefined => {
  const { filters } = useFilterProvider()
  const spaceType = filters.spaceTypes?.[0] as SpaceType | undefined
  const locationType = spaceType
    ? SPACE_TYPE_TO_ES_LOCATION_TYPE[spaceType]
    : undefined

  const { data } = useQuery<NearbySearchCountsData, NearbySearchCountsVars>(
    NEARBY_SEARCH_COUNTS,
    {
      variables: {
        input: {
          lat: filters.lat!,
          lng: filters.lng!,
          locationType
        }
      },
      skip: !!staticData || !filters.lat || !filters.lng
    }
  )

  return staticData ?? data?.nearbySearchCounts
}
